/*
 * pdp-gallery-zoom.js
 * -------------------
 * Comportamiento del visor a pantalla completa de la galeria de producto.
 * Se carga junto con `pdp-gallery-zoom.css` desde `sections/main-product.liquid`
 * cuando `settings.use_ig_pdp` y `settings.pdp_gallery_zoom` estan activos.
 *
 * Reemplaza tres cosas que el tema base hacia mal:
 *
 *  1. ZOOM. `assets/main-product.js` togglea la clase `.zoom` en un *click*
 *     simple, y esa clase **no tiene una sola regla de CSS en todo el tema**:
 *     el zoom no existia. Aca es doble click sobre la foto, con paneo siguiendo
 *     el cursor.
 *
 *  2. CLICK EN MINIATURA. El handler de `assets/global.js` (`sidebar()`) usaba
 *     `$(target).position().top + $(dialog).scrollTop()`. `position()` mide
 *     contra el offsetParent, que no es el scroller, y ademas animaba con
 *     jQuery sobre un contenedor con `scroll-behavior: smooth`, o sea dos
 *     animaciones peleandose por el mismo scrollTop. Aca se mide contra el
 *     scroller y se scrollea una sola vez.
 *
 *  3. MINIATURA ACTIVA. El scroll-spy de `sidebar()` comparaba
 *     `$(el).offset().top` (coordenadas de documento) contra
 *     `$(window).scrollTop()`, que vale 0 con el visor abierto porque el body
 *     esta bloqueado y el modal es `position: fixed`. Medido: la miniatura
 *     activa se quedaba en la primera en las cuatro posiciones de scroll.
 *     Aca es un IntersectionObserver con `root` en el dialogo.
 *
 * `sidebar()` no se desactiva desde aca: la seccion deja de imprimir la clase
 * `product__media-sublist__sidebar-nav` cuando la capa esta encendida, y esa
 * clase es un hook de JS puro (unica referencia: global.js:498). Apagando el
 * setting vuelve el markup y con el, el comportamiento del base.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 750px)'; // igual que el corte de layout de pdp-gallery-zoom.css
  var FINE_POINTER = '(hover: hover) and (pointer: fine)';
  /*
   * El zoom llega hasta la resolucion nativa de la foto (`naturalWidth /
   * anchoPintado`), acotado entre MIN y MAX.
   *
   * MIN existe porque las fotos de la tienda son de 960x1200: a 1455x900 la foto
   * ajustada mide 720x900, o sea que el nativo da apenas 1.33x y el gesto no se
   * notaria. Con 1.5 el navegador reescala un 12.5%, que sobre un packshot no se
   * ve, y el zoom si se percibe. Si algun dia se suben originales mas grandes,
   * el factor nativo pasa a mandar solo y sube hasta MAX sin tocar nada.
   */
  var MIN_SCALE = 1.5;
  var MAX_SCALE = 3;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  function init(modal) {
    if (!modal || modal.dataset.galleryZoomReady === 'true') return;
    modal.dataset.galleryZoomReady = 'true';

    var dialog = modal.querySelector('.product-media-modal__dialog');
    var content = modal.querySelector('.product-media-modal__content');
    if (!dialog || !content) return;

    var items = function () {
      return Array.prototype.slice.call(content.querySelectorAll('.product-media-modal__item'));
    };
    var subitems = function () {
      return Array.prototype.slice.call(modal.querySelectorAll('.product__media-subitem'));
    };

    /* ---------------------------------------------------------------- */
    /* Scroll a una foto                                                */
    /* ---------------------------------------------------------------- */
    function scrollToItem(item, smooth) {
      if (!item) return;
      var pad = parseFloat(getComputedStyle(dialog).paddingTop) || 0;
      var delta = item.getBoundingClientRect().top - dialog.getBoundingClientRect().top - pad;
      if (Math.abs(delta) < 1) return;
      dialog.scrollTo({
        top: dialog.scrollTop + delta,
        behavior: smooth && !reducedMotion() ? 'smooth' : 'auto',
      });
    }

    function itemById(id) {
      if (!id) return null;
      // `data-media-modal-id` es el atributo confiable. El `id` del nodo lo
      // imprime la seccion y en la foto destacada salia truncado durante mucho
      // tiempo (usaba `media.id` fuera del for, o sea nil).
      return content.querySelector('[data-media-modal-id="' + CSS.escape(id) + '"]');
    }

    /* ---------------------------------------------------------------- */
    /* Miniaturas                                                       */
    /* ---------------------------------------------------------------- */
    // Captura sobre el modal: si alguien vuelve a imprimir la clase del hook de
    // jQuery, este handler igual corre primero y corta la propagacion, asi que
    // nunca hay dos animaciones de scroll a la vez.
    modal.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest && event.target.closest('.product__media-sublist a[href^="#"]');
        if (!link || !modal.contains(link)) return;
        event.preventDefault();
        event.stopPropagation();

        var sub = link.closest('.product__media-subitem');
        var target = sub ? itemById(sub.dataset.mediaSubId) : null;
        if (!target) {
          // Respaldo por href, por si el markup cambia.
          target = content.querySelector(link.getAttribute('href'));
        }
        resetZoom();
        scrollToItem(target, true);
      },
      true
    );

    function markActive(item) {
      var id = item && item.dataset.mediaModalId;
      subitems().forEach(function (sub) {
        sub.classList.toggle('active', !!id && sub.dataset.mediaSubId === id);
      });
    }

    var ratios = new Map();
    var spy = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        var best = null;
        var max = 0;
        ratios.forEach(function (ratio, item) {
          if (ratio > max) {
            max = ratio;
            best = item;
          }
        });
        if (best) markActive(best);
      },
      { root: dialog, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    function observeItems() {
      ratios.clear();
      spy.disconnect();
      items().forEach(function (item) {
        ratios.set(item, 0);
        spy.observe(item);
      });
    }
    observeItems();

    /* ---------------------------------------------------------------- */
    /* Zoom por doble click                                             */
    /* ---------------------------------------------------------------- */
    var zoomed = null; // { item, img, rect }

    function canZoom() {
      return window.matchMedia(DESKTOP).matches && window.matchMedia(FINE_POINTER).matches;
    }

    function resetZoom() {
      if (!zoomed) return;
      zoomed.item.classList.remove('is-zoomed', 'is-zoom-animating');
      zoomed.img.style.transform = '';
      zoomed.img.style.transformOrigin = '';
      zoomed = null;
    }

    function originFor(event) {
      // El rect se guarda ANTES de escalar y no se vuelve a leer: `transform` no
      // toca el layout, pero `getBoundingClientRect()` sobre una imagen ya
      // escalada devuelve la caja pintada y el paneo se realimentaria consigo
      // mismo. Mismo criterio que la tira de `scroll-gallery`.
      var rect = zoomed.rect;
      var x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      var y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      return x.toFixed(2) + '% ' + y.toFixed(2) + '%';
    }

    function zoomFactor(img) {
      var width = img.offsetWidth;
      if (!width || !img.naturalWidth) return MIN_SCALE;
      return clamp(img.naturalWidth / width, MIN_SCALE, MAX_SCALE);
    }

    content.addEventListener('dblclick', function (event) {
      if (!canZoom()) return;
      var img = event.target.closest('.product-media-modal__item > img');
      if (!img) return;

      // Un doble click deja seleccionado el nodo de al lado; sobre una imagen a
      // pantalla completa se ve como un flash azul.
      var selection = window.getSelection && window.getSelection();
      if (selection) selection.removeAllRanges();

      var item = img.parentElement;
      if (zoomed && zoomed.img === img) {
        resetZoom();
        return;
      }
      resetZoom();

      zoomed = { item: item, img: img, rect: img.getBoundingClientRect() };
      item.classList.add('is-zoomed');
      img.style.transformOrigin = originFor(event);
      img.style.transform = 'scale(' + zoomFactor(img).toFixed(3) + ')';
      // La transicion se habilita recien en el frame siguiente para que el
      // primer salto de escala salga del punto que se hizo doble click y no
      // animado desde el centro.
      requestAnimationFrame(function () {
        if (zoomed && zoomed.img === img && !reducedMotion()) item.classList.add('is-zoom-animating');
      });
    });

    content.addEventListener('pointermove', function (event) {
      if (!zoomed || event.pointerType !== 'mouse') return;
      if (!zoomed.item.contains(event.target)) return;
      zoomed.img.style.transformOrigin = originFor(event);
    });

    content.addEventListener(
      'pointerleave',
      function (event) {
        if (zoomed && event.target === zoomed.item) resetZoom();
      },
      true
    );

    // Scrollear con una foto ampliada dejaria el zoom colgado sobre una foto que
    // ya no se ve. El `scroll` propio del scrollTo programatico tambien entra
    // aca, por eso los dos call sites resetean antes de scrollear.
    dialog.addEventListener('scroll', function () {
      if (zoomed) resetZoom();
    });

    /* ---------------------------------------------------------------- */
    /* Teclado                                                          */
    /* ---------------------------------------------------------------- */
    // Escape en captura sobre document: `ModalDialog` cierra en el `keyup` que
    // escucha en el propio modal (global.js:1754), o sea fase de burbujeo. Un
    // listener de captura en document corre antes y puede cortar la propagacion,
    // asi que la primera pulsacion sale del zoom y la segunda cierra el visor.
    document.addEventListener(
      'keyup',
      function (event) {
        if (!modal.hasAttribute('open')) return;
        if (event.code !== 'Escape' && event.key !== 'Escape') return;
        if (!zoomed) return;
        resetZoom();
        event.stopPropagation();
      },
      true
    );

    document.addEventListener('keydown', function (event) {
      if (!modal.hasAttribute('open') || zoomed) return;
      var step = 0;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight' || event.key === 'PageDown') step = 1;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft' || event.key === 'PageUp') step = -1;
      if (!step) return;

      var list = items();
      var current = 0;
      var max = 0;
      list.forEach(function (item, index) {
        var ratio = ratios.get(item) || 0;
        if (ratio > max) {
          max = ratio;
          current = index;
        }
      });
      var next = list[clamp(current + step, 0, list.length - 1)];
      if (next && next !== list[current]) {
        event.preventDefault();
        scrollToItem(next, true);
      }
    });

    /* ---------------------------------------------------------------- */
    /* Apertura y cierre                                                */
    /* ---------------------------------------------------------------- */
    // `ProductModal.showActiveMedia()` hace `scrollIntoView()` sobre el <img>,
    // no sobre la celda. Con la celda a 100vh y la foto centrada adentro, eso
    // alinea el borde de arriba de la FOTO con el borde del scroller y deja la
    // mitad del aire de la celda arriba de la pantalla. Se corrige apuntando a
    // la celda apenas el visor abre.
    function alignOnOpen() {
      var openedBy = modal.openedBy;
      var mediaId = openedBy && openedBy.getAttribute('data-media-id');
      var target = null;
      if (mediaId) {
        var img = content.querySelector('[data-media-id="' + CSS.escape(mediaId) + '"]');
        target = img ? img.closest('.product-media-modal__item') : null;
      }
      if (!target) target = items()[0];
      if (!target) return;
      scrollToItem(target, false);
      markActive(target);
    }

    new MutationObserver(function () {
      var open = modal.hasAttribute('open');
      document.documentElement.classList.toggle('pdp-gallery-zoom-open', open);
      if (open) {
        requestAnimationFrame(alignOnOpen);
      } else {
        resetZoom();
      }
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });

    window.addEventListener('resize', resetZoom);
    document.addEventListener('shopify:section:load', observeItems);
  }

  function boot() {
    document.querySelectorAll('product-modal.pdp-gallery-zoom').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
